import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import api from '../lib/apiClient';

interface OptionShape {
  id: number | string;
  [key: string]: unknown;
}

interface SearchableSelectProps<T extends OptionShape> {
  endpoint: string;
  value: string | number | '' | null | undefined;
  onChange: (id: string | number, option?: T) => void;
  getLabel: (option: T) => string;
  getSubLabel?: (option: T) => string;
  placeholder?: string;
  params?: Record<string, unknown>;
  disabled?: boolean;
}

// Type-to-search dropdown backed by a paginated API endpoint. Loads the
// currently-selected option by id (if any) so it can show its label even
// before the user opens the dropdown, then re-queries `endpoint` with a
// `search` param as the user types.
export default function SearchableSelect<T extends OptionShape>({
  endpoint,
  value,
  onChange,
  getLabel,
  getSubLabel,
  placeholder = 'Search…',
  params,
  disabled,
}: SearchableSelectProps<T>) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<T | null>(null);
  const [options, setOptions] = useState<T[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!value) {
      setSelected(null);
      return;
    }
    if (selected && String(selected.id) === String(value)) return;
    api.get<T>(`${endpoint}/${value}`).then((res) => setSelected(res.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, value]);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const timeout = setTimeout(() => {
      api
        .get<{ data: T[] }>(endpoint, { params: { search: query, per_page: 20, ...params } })
        .then((res) => setOptions(res.data.data))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, query, open]);

  const choose = (option: T) => {
    setSelected(option);
    setQuery('');
    setOpen(false);
    onChange(option.id, option);
  };

  const clear = (e: ReactMouseEvent) => {
    e.stopPropagation();
    setSelected(null);
    setQuery('');
    onChange('');
  };

  return (
    <div className="searchable-select" ref={rootRef}>
      <div className="searchable-select-control" onClick={() => !disabled && setOpen(true)}>
        {selected && !open ? (
          <span className="searchable-select-value">{getLabel(selected)}</span>
        ) : (
          <input
            type="text"
            value={query}
            placeholder={selected ? getLabel(selected) : placeholder}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setOpen(true)}
            disabled={disabled}
          />
        )}
        {selected && !disabled && (
          <button type="button" className="searchable-select-clear" onClick={clear} aria-label="Clear">
            ×
          </button>
        )}
      </div>
      {open && !disabled && (
        <div className="searchable-select-menu">
          {loading && <div className="searchable-select-empty">Searching…</div>}
          {!loading && options.length === 0 && <div className="searchable-select-empty">No matches</div>}
          {!loading &&
            options.map((option) => (
              <button
                type="button"
                key={option.id}
                className="searchable-select-option"
                onClick={() => choose(option)}
              >
                <span>{getLabel(option)}</span>
                {getSubLabel && <small>{getSubLabel(option)}</small>}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}
